<template>
  <div class="input-tags">
    <BaseInputSelect
      v-if="!hideInput"
      ref="inputSelect"
      v-model="tags"
      v-model:input-text="inputTextModel"
      :auto-focus="autoFocus"
      class="tw:relative"
      :clearable="clearable"
      :close-on-select="closeOnSelect"
      data-testid="input-select"
      :description="description"
      :disabled="disabled"
      :hide-auto-complete="hideAutoComplete"
      :label="label"
      :loading="loading"
      multiple
      :new-design="newDesign"
      :option-label="optionLabel"
      :option-prop="optionProp"
      :options="filteredOptions"
      :placeholder="placeholder"
      :selected-text="selectedText"
      :use-input="!hideInput"
      :validations="validations"
      @clear="onClear"
      @filter="handleInput"
      @focus="emit('focus')"
      @selected="onSelected"
      @submit="addTag"
    >
      <template
        v-if="chooseColor"
        #beforeOptions
      >
        <span
          v-if="text"
          class="tw:flex tw:w-full tw:items-center"
        >
          <span class="tw:pr-1">{{ $t("input_tags.create_tag") }}</span>
          <Tag
            :color="selectedColor || undefined"
            icon="plus"
            :label="text"
            @click="addTag(text)"
            @remove="addTag(text)"
          />
        </span>
        <QColor
          v-model="selectedColor"
          default-view="palette"
          flat
          format-model="hex"
          no-footer
          no-header
          :palette="customPaletteColours"
          @click="addTag(text)"
        />
      </template>

      <template
        v-if="chooseColor"
        #option="{ option }"
      >
        <Tag
          :color="tagColor(option)"
          hide-remove-button
          :icon="tagIcon(option)"
          :label="tagDisplay(option)"
        />
      </template>

      <template
        v-if="hideEmpty"
        #empty
      >
        <span />
      </template>

      <template
        v-for="(_, name) in $slots"
        #[name]="slotData"
      >
        <slot
          :name="name"
          v-bind="slotData"
        />
      </template>
    </BaseInputSelect>

    <div :class="[tagsClass, { 'q-mt-xs': !hideInput }]">
      <div
        v-for="(tag, index) in tags"
        :key="index"
        class="tw:flex"
      >
        <slot
          :color="tagColor(tag)"
          :index="index"
          :label="tagDisplay(tag)"
          name="tag"
          :remove-tag="removeTag"
          :tag="tag"
        >
          <Tag
            :color="tagColor(tag)"
            data-testid="tag"
            :hide-cursor="hideTagCursor"
            :hide-remove-button="hideRemoveButton"
            :icon="tagIcon(tag)"
            :label="tagDisplay(tag)"
            @click="emit('click', tag)"
            @remove="removeTag(index, tag)"
          />
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DesignSystemIcons } from "design-system/types";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import BaseInputSelect from "shared/components/base/BaseInputSelect";
import Tag from "shared/components/base/BaseTag";
import type { Validations } from "shared/composables/useValidation";
import { tagPalette } from "shared/constants";
import { getCssVariableValue } from "shared/helpers/dom";
import type { Maybe, Nullable } from "shared/types";

defineOptions({ name: "BaseInputTags" });

interface InputTagsOption {
  color?: string;
  icon?: DesignSystemIcons;
  label?: number | string;
}

export type InputTagsValue = InputTagsOption | string;

export interface InputTagsProps {
  autoFocus?: boolean;
  chooseColor?: boolean;
  clearable?: boolean;
  closeOnSelect?: boolean;
  debounce?: number | string;
  defaultColor?: Nullable<string>;
  description?: string;
  disabled?: boolean;
  hideAutoComplete?: boolean;
  hideEmpty?: boolean;
  hideInput?: boolean;
  hideRemoveButton?: boolean;
  hideTagCursor?: boolean;
  label?: string;
  loading?: boolean;
  modelValue: InputTagsValue[];
  newDesign?: boolean;
  optionLabel?: ((tag: InputTagsValue) => string) | string;
  optionProp?: string;
  options?: InputTagsValue[];
  optionsOnly?: boolean;
  placeholder?: string;
  selectedText?: Nullable<(count: number) => string>;
  showOptionsOnClick?: boolean;
  tagsClass?: string | string[] | Record<string, boolean>;
  validations?: Validations;
}

export interface InputTagsEmits {
  add: [tag: Maybe<InputTagsValue>, color?: Nullable<string>];
  clear: [];
  click: [tag: InputTagsValue];
  filter: [text: string];
  focus: [];
  remove: [index: number, tag: InputTagsValue];
  "update:loading": [loading: boolean];
  "update:modelValue": [value: InputTagsValue[]];
}

const props = withDefaults(defineProps<InputTagsProps>(), {
  debounce: 300,
  defaultColor: null,
  description: "",
  label: "",
  optionLabel: "",
  optionProp: "id",
  options: () => [],
  placeholder: "",
  selectedText: null,
  tagsClass: "tw:flex tw:flex-wrap tw:gap-1",
  validations: () => [],
});

const emit = defineEmits<InputTagsEmits>();
const inputTextModel = defineModel<string>("inputText");

const inputSelect =
  useTemplateRef<ComponentExposed<typeof BaseInputSelect>>("inputSelect");

const inputTimer = ref<Nullable<ReturnType<typeof setTimeout>>>(null);
const selectedColor = ref<Nullable<string>>(props.defaultColor);
const text = ref<string>("");

const tags = computed<InputTagsValue[]>({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const filteredOptions = computed<InputTagsValue[]>(() =>
  props.options.filter((option) =>
    tagDisplay(option).toLowerCase().includes(text.value.toLowerCase())
  )
);

const plainTagColor = computed<string>(() =>
  props.newDesign ? getCssVariableValue("--s-color-navy-7") : "#444"
);

const customPaletteColours = computed<string[]>(() => Object.keys(tagPalette));

watch(
  () => props.defaultColor,
  (value) => {
    selectedColor.value = value;
  },
  { immediate: true }
);

function tagDisplay(tag: InputTagsValue): string {
  if (typeof tag === "string") return tag;

  if (props.optionLabel) {
    if (typeof props.optionLabel === "function") {
      return props.optionLabel(tag);
    }

    return getTagProperty(tag, props.optionLabel);
  }

  if (props.optionProp) return getTagProperty(tag, props.optionProp);

  return "";
}

function getTagProperty(tag: InputTagsOption, property: string): string {
  const entries = Object.entries(tag) as Array<
    [string, DesignSystemIcons | number | string | undefined]
  >;

  const value = entries.find(([key]) => key === property)?.[1];

  return value === undefined ? "" : String(value);
}

function tagColor(tag: InputTagsValue): string | undefined {
  return props.chooseColor && typeof tag !== "string"
    ? tag.color
    : plainTagColor.value;
}

function tagIcon(tag: InputTagsValue): DesignSystemIcons | undefined {
  if (typeof tag === "string") return undefined;

  return tag.icon as DesignSystemIcons | undefined;
}

function isOption(tag: InputTagsValue): boolean {
  const tagValue = tagDisplay(tag);

  return props.options.some((option) => {
    const optionValue = tagDisplay(option);

    return (
      Boolean(tagValue) &&
      optionValue.toLowerCase().includes(tagValue.toLowerCase())
    );
  });
}

function findTagFromOptions(tag: InputTagsValue): InputTagsValue | undefined {
  const tagValue = tagDisplay(tag);

  return props.options.find((option) => {
    const optionValue = tagDisplay(option);

    return (
      Boolean(tagValue) &&
      optionValue.toLowerCase().includes(tagValue.toLowerCase())
    );
  });
}

function onSelected(tag: InputTagsValue | undefined): void {
  if (props.chooseColor) {
    const color =
      tag && typeof tag !== "string"
        ? tag.color || selectedColor.value
        : selectedColor.value;

    emit("add", tag, color);

    return;
  }

  emit("add", tag);
}

function addTag(tag: InputTagsValue | undefined): void {
  if (props.closeOnSelect) {
    inputSelect.value?.blur();
    inputSelect.value?.hideList();
  }

  let tagToAdd = tag;

  if (!tagToAdd) {
    onSelected(tagToAdd);

    return;
  }

  if (props.optionsOnly && !isOption(tagToAdd)) return;

  if (props.optionsOnly) tagToAdd = findTagFromOptions(tagToAdd);

  if (tagToAdd) {
    tags.value = [...tags.value, tagToAdd];
    text.value = "";
  }

  onSelected(tagToAdd);
}

function removeTag(index: number, tag: InputTagsValue): void {
  emit("remove", index, tag);
}

function handleInput(value: string): void {
  text.value = value;
  emit("update:loading", true);
  if (inputTimer.value) clearTimeout(inputTimer.value);

  inputTimer.value = setTimeout(() => {
    emit("filter", value);
  }, Number(props.debounce));
}

function onClear(): void {
  text.value = "";
  emit("clear");
}

onMounted(() => {
  if (props.showOptionsOnClick) handleInput("");
});
</script>

<style lang="scss" scoped>
.empty {
  padding: 0;
}

:deep(.q-spinner) {
  margin-left: 5px;
}
</style>
